import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";

import ButtonV1 from "components/ButtonV1";
import SvgIcon from "components/SvgIcon";
import useToaster from "hooks/useToaster";

const formatFileSize = (bytes = 0) =>
  bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(1)} KB`
    : `${(bytes / 1024 / 1024).toFixed(2)} MB`;

const formatAcceptedTypes = (types = []) =>
  types.map((type) => "." + type).join(", ");

const getFileValidationError = (selected, acceptedTypes, maxSizeMB) => {
  const extension = selected?.name?.split(".").pop()?.toLowerCase();
  if (!acceptedTypes.includes(extension)) {
    return `Please upload a ${acceptedTypes.join(" or ")} file`;
  }
  if (selected.size / 1024 / 1024 > maxSizeMB) {
    return `Allowed max file size is ${maxSizeMB}MB`;
  }
  return null;
};

const DropzoneHeader = ({ title, subtitle, onDownloadTemplate }) => {
  if (!title && !onDownloadTemplate) return null;

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={4}
    >
      <Box display="flex" alignItems="center">
        {title && (
          <Typography fontSize={18} fontWeight="bold">
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography fontSize={14} ml={2} color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {onDownloadTemplate && (
        <Typography
          variant="body2"
          color="primary"
          onClick={onDownloadTemplate}
          sx={{ textDecoration: "underline", cursor: "pointer" }}
        >
          Download template
        </Typography>
      )}
    </Box>
  );
};

DropzoneHeader.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  onDownloadTemplate: PropTypes.func,
};

const SelectedFileCard = ({ file, onRemove, isUploading, theme }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    mt={4}
    px={4}
    py={2}
    style={{
      borderRadius: 12,
      border: `1px solid ${theme.palette.grey[400]}`,
    }}
  >
    <Box>
      <Typography fontSize={14} fontWeight={600}>
        {file.name}
      </Typography>
      <Typography fontSize={12} color="text.secondary">
        {formatFileSize(file.size)}
      </Typography>
    </Box>
    <IconButton
      size="small"
      aria-label="remove file"
      onClick={onRemove}
      disabled={isUploading}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

SelectedFileCard.propTypes = {
  file: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  isUploading: PropTypes.bool,
  theme: PropTypes.object.isRequired,
};

const CompactDropzone = ({
  header,
  fileInput,
  file,
  allowedText,
  isDisabled,
  isDragging,
  isUploading,
  uploadButtonText,
  theme,
  inputRef,
  onDragOver,
  onDragLeave,
  onDrop,
  onRemoveFile,
  onUpload,
  hintText,
}) => (
  <Box>
    {header}
    <Box display="flex" alignItems="center" gap={4}>
      <Box
        data-testid="file-dropzone"
        flex={1}
        minWidth={0}
        height={40}
        px={4}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => !isDisabled && !file && inputRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          border: 1,
          borderStyle: "dashed",
          borderColor: isDragging
            ? theme.palette.primary.main
            : theme.palette.grey[900],
          borderRadius: 8,
          backgroundColor: theme.palette.info.lightBlueBackround,
          cursor: isDisabled || file ? "default" : "pointer",
          opacity: isDisabled ? 0.6 : 1,
        }}
      >
        {fileInput}
        <Typography
          noWrap
          fontSize={14}
          fontWeight={file ? 600 : 400}
          color={file ? "text.primary" : theme.palette.grey[1000]}
        >
          {file
            ? `${file.name} (${formatFileSize(file.size)})`
            : `Click or drag file here · ${allowedText}`}
        </Typography>
        {file && (
          <IconButton
            size="small"
            aria-label="remove file"
            onClick={onRemoveFile}
            disabled={isUploading}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <ButtonV1
        title={isUploading ? "Uploading..." : uploadButtonText}
        onClick={onUpload}
        disabled={!file || isDisabled || isUploading}
        loader={isUploading}
        size="small"
      />
    </Box>
    {hintText}
  </Box>
);

CompactDropzone.propTypes = {
  header: PropTypes.node,
  fileInput: PropTypes.node,
  file: PropTypes.object,
  allowedText: PropTypes.string,
  isDisabled: PropTypes.bool,
  isDragging: PropTypes.bool,
  isUploading: PropTypes.bool,
  uploadButtonText: PropTypes.string,
  theme: PropTypes.object.isRequired,
  inputRef: PropTypes.object,
  onDragOver: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  hintText: PropTypes.node,
};

/**
 * @description Reusable drag & drop file upload card.
 * Validates by extension + size, keeps selected file in state and
 * hands it to `onUpload` on submit. Parent owns the API call.
 */
const FileDropzone = forwardRef(
  (
    {
      title,
      subtitle,
      acceptedTypes = ["csv"],
      maxSizeMB = 5,
      onUpload,
      onFileSelect,
      onDownloadTemplate,
      isUploading = false,
      disabled = false,
      uploadButtonText = "Upload",
      helperText,
      hint,
      compact = false,
      fillHeight = false,
    },
    ref,
  ) => {
    const theme = useTheme();
    const triggerToaster = useToaster();
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const isDisabled = disabled || isUploading;
    const accept = acceptedTypes.map((type) => "." + type).join(",");
    const formattedTypes = formatAcceptedTypes(acceptedTypes);
    const allowedText =
      helperText || `${formattedTypes} · max ${maxSizeMB} MB`;

    const hintText = hint ? (
      <Typography fontSize={12} color="text.secondary" mt={2}>
        {hint}
      </Typography>
    ) : null;

    useImperativeHandle(ref, () => ({
      getFile: () => file,
      resetFile: () => setFile(null),
    }));

    const handleFiles = (fileList) => {
      const files = [...(fileList || [])];
      if (!files.length) return;
      if (files.length > 1) {
        triggerToaster("Only 1 file can be uploaded at a time", "error");
        return;
      }
      const errorMsg = getFileValidationError(files[0], acceptedTypes, maxSizeMB);
      if (errorMsg) {
        triggerToaster(errorMsg, "error");
        return;
      }
      setFile(files[0]);
      onFileSelect?.(files[0]);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (!isDisabled) {
        handleFiles(e.dataTransfer.files);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      if (!isDisabled) {
        setIsDragging(true);
      }
    };

    const handleUpload = async () => {
      if (!file || !onUpload || isUploading) return;
      const isSuccess = await onUpload(file);
      if (isSuccess !== false) {
        setFile(null);
      }
    };

    const header = (
      <DropzoneHeader
        title={title}
        subtitle={subtitle}
        onDownloadTemplate={onDownloadTemplate}
      />
    );

    const fileInput = (
      <input
        hidden
        type="file"
        accept={accept}
        ref={inputRef}
        data-testid="file-dropzone-input"
        onClick={(e) => {
          e.target.value = "";
        }}
        onChange={(e) => handleFiles(e.target.files)}
      />
    );

    if (compact) {
      return (
        <CompactDropzone
          header={header}
          fileInput={fileInput}
          file={file}
          allowedText={allowedText}
          isDisabled={isDisabled}
          isDragging={isDragging}
          isUploading={isUploading}
          uploadButtonText={uploadButtonText}
          theme={theme}
          inputRef={inputRef}
          onDragOver={handleDragOver}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onRemoveFile={() => setFile(null)}
          onUpload={handleUpload}
          hintText={hintText}
        />
      );
    }

    return (
      <Box
        {...(fillHeight && {
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        })}
      >
        {header}

        <Box
          data-testid="file-dropzone"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          flex={fillHeight ? 1 : undefined}
          p={fillHeight ? 4 : 8}
          onClick={() => !isDisabled && inputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          style={{
            border: 1,
            borderStyle: "dashed",
            borderColor: isDragging
              ? theme.palette.primary.main
              : theme.palette.grey[900],
            borderRadius: 12,
            backgroundColor: theme.palette.info.lightBlueBackround,
            cursor: isDisabled ? "not-allowed" : "pointer",
            opacity: isDisabled ? 0.6 : 1,
          }}
        >
          {fileInput}
          <SvgIcon
            name="file"
            style={{ height: 48, width: 48, marginBottom: 20 }}
          />
          <Typography style={{ fontSize: 16, lineHeight: 1.25 }}>
            Click or drag file to this area to upload
          </Typography>
          <Typography
            style={{
              fontSize: 14,
              color: theme.palette.grey[1000],
              lineHeight: 1.2,
            }}
          >
            {helperText ||
              `Allowed file type: ${formattedTypes}. Maximum file size ${maxSizeMB} MB.`}
          </Typography>
        </Box>
        {hintText}

        {file && (
          <SelectedFileCard
            file={file}
            onRemove={() => setFile(null)}
            isUploading={isUploading}
            theme={theme}
          />
        )}

        <Box display="flex" justifyContent="flex-end" mt={4}>
          <ButtonV1
            title={isUploading ? "Uploading..." : uploadButtonText}
            onClick={handleUpload}
            disabled={!file || isDisabled || isUploading}
            loader={isUploading}
            size="small"
          />
        </Box>
      </Box>
    );
  },
);

FileDropzone.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  acceptedTypes: PropTypes.arrayOf(PropTypes.string),
  maxSizeMB: PropTypes.number,
  onUpload: PropTypes.func,
  onFileSelect: PropTypes.func,
  onDownloadTemplate: PropTypes.func,
  isUploading: PropTypes.bool,
  disabled: PropTypes.bool,
  uploadButtonText: PropTypes.string,
  helperText: PropTypes.string,
  hint: PropTypes.node,
  compact: PropTypes.bool,
  fillHeight: PropTypes.bool,
};

FileDropzone.displayName = "FileDropzone";

export default FileDropzone;